import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { StockBalance, Warehouse } from '../../../types/bookkeeping.js';

export function StockPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const [warehouseId, setWarehouseId] = useState('');
  const [asOf, setAsOf] = useState('');

  const { data: warehousesData } = useQuery({
    queryKey: ['bk-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });
  const warehouses = warehousesData?.warehouses ?? [];

  const stockUrl = asOf
    ? `/bookkeeping/inventory/stock/summary?as_of=${asOf}`
    : `/bookkeeping/inventory/stock${warehouseId ? `?warehouse_id=${warehouseId}` : ''}`;

  const { data, isLoading } = useQuery({
    queryKey: ['bk-stock', activeOrgId, warehouseId, asOf],
    queryFn: () => api<{ balances?: StockBalance[]; snapshot?: StockBalance[] }>(stockUrl),
    enabled: !!activeOrgId,
  });

  const balances = (data?.balances ?? data?.snapshot ?? []) as StockBalance[];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('inventory.stock.title')}</h1>

      {/* Filters */}
      <div className="grid grid-cols-3 gap-3">
        <select
          value={warehouseId}
          onChange={(e) => {
            setWarehouseId(e.target.value);
            setAsOf(''); // clear historical when switching warehouse filter
          }}
          className="rounded border px-2 py-1 text-sm"
          disabled={!!asOf}
        >
          <option value="">{t('inventory.stock.all_warehouses')}</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} — {w.name}
            </option>
          ))}
        </select>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-xs text-slate-500">{t('inventory.stock.as_of')}</span>
          <input
            type="date"
            value={asOf}
            onChange={(e) => {
              setAsOf(e.target.value);
              setWarehouseId(''); // historical summary uses all warehouses
            }}
            className="rounded border px-2 py-1 text-sm"
          />
        </label>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('inventory.stock.item')}</th>
                <th className="px-3 py-2">{t('inventory.stock.warehouse')}</th>
                <th className="px-3 py-2 text-right">{t('inventory.stock.qty_on_hand')}</th>
                <th className="px-3 py-2 text-right">{t('inventory.stock.avg_cost')}</th>
              </tr>
            </thead>
            <tbody>
              {balances.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-slate-400">
                    {t('inventory.stock.empty')}
                  </td>
                </tr>
              )}
              {balances.map((b) => (
                <tr
                  key={`${b.item_id}-${b.warehouse_id}`}
                  className="border-b last:border-0 hover:bg-slate-50"
                >
                  <td className="px-3 py-2">{b.item_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.warehouse_code}</td>
                  <td className="px-3 py-2 text-right font-mono">{b.qty_on_hand}</td>
                  <td className="px-3 py-2 text-right font-mono">{b.avg_cost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
